import ReactJsonView from '@microlink/react-json-view';
import { Box, Button, Group, Text, useMantineTheme } from '@mantine/core';
import {
  TemplatePanel,
  TemplateTextStyles,
} from '../../organisms/graph-config-panel/styles';
import { useGraphContext } from '../../context';
import { EvaluationValueKind, NodeId } from '../../types';
import { LuCopy } from 'react-icons/lu';
import { notifications } from '@mantine/notifications';

export type NodeOutputMode = 'template' | 'evaluation';

export type NodeOutputProps = {
  nodeId: NodeId;
  mode: NodeOutputMode;
  emptyLabel?: string;
  withPanel?: boolean;
};

export const NodeOutput = ({
  nodeId,
  mode,
  emptyLabel = 'Empty',
  withPanel = true,
}: NodeOutputProps) => {
  const value = useGraphContext(({ eval: ev }) =>
    mode === 'template' ? ev.getNodeTemplate(nodeId) : ev.getNodeValue(nodeId),
  );
  const outputValue =
    mode === 'evaluation' &&
    value &&
    typeof value === 'object' &&
    'kind' in value
      ? value.kind === EvaluationValueKind.Empty
        ? null
        : (value as { value: unknown }).value
      : value;
  const theme = useMantineTheme();
  const isStructured = outputValue !== null && typeof outputValue === 'object';
  const primitiveValue = isStructured
    ? null
    : JSON.stringify(outputValue, null, 2);

  const content = isStructured ? (
    <ReactJsonView
      src={outputValue as Record<string, unknown>}
      name={false}
      collapsed={false}
      displayDataTypes={false}
      enableClipboard={false}
      style={TemplateTextStyles(theme)}
      theme={theme.colorScheme === 'dark' ? 'ocean' : 'rjv-default'}
    />
  ) : (
    <Text sx={TemplateTextStyles(theme)}>
      {primitiveValue === null ? emptyLabel : primitiveValue}
    </Text>
  );

  if (outputValue === undefined || outputValue === null) {
    return withPanel ? (
      <TemplatePanel>
        <Box py="md" px="md" sx={{ height: '100%' }}>
          <Text sx={TemplateTextStyles(theme)}>{emptyLabel}</Text>
        </Box>
      </TemplatePanel>
    ) : (
      <Text sx={TemplateTextStyles(theme)}>{emptyLabel}</Text>
    );
  }

  if (!withPanel) {
    return content;
  }

  return (
    <TemplatePanel>
      <Box py="md" px="md" sx={{ height: '100%' }}>
        <Group position="right" mb="sm">
          <Button
            size="xs"
            variant="light"
            leftIcon={<LuCopy size={14} />}
            onClick={() => {
              navigator.clipboard.writeText(
                JSON.stringify(outputValue, null, 2),
              );
              notifications.show({
                message: 'Template JSON copied to clipboard',
                color: 'blue',
                autoClose: 2000,
              });
            }}
          >
            Copy
          </Button>
        </Group>
        {content}
      </Box>
    </TemplatePanel>
  );
};
